import {
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";
import { getTvDisplayCelebrationsCollectionRef } from "./tvDisplayService";

export const CELEBRATION_TYPES = { TEXT: "TEXT", IMAGE: "IMAGE", VIDEO: "VIDEO" };
export const CELEBRATION_STATES = { DRAFT: "DRAFT", SCHEDULED: "SCHEDULED", ACTIVE: "ACTIVE", PAUSED: "PAUSED", EXPIRED: "EXPIRED" };

const callManageCelebration = httpsCallable(functions, "manageTvDisplayCelebration");

export function subscribeToCelebrations(callback, errorCallback) {
  return onSnapshot(
    query(getTvDisplayCelebrationsCollectionRef(), orderBy("createdAt", "desc")),
    (snapshot) => callback?.(snapshot.docs.map(normalizeCelebration)),
    (error) => errorCallback?.(error)
  );
}

export async function createCelebration(data = {}) {
  const type = Object.values(CELEBRATION_TYPES).includes(data.type) ? data.type : CELEBRATION_TYPES.TEXT;
  const title = requiredText(data.title, 100, "Celebration name");
  const headline = requiredText(data.headline, 120, "Display title");
  const message = cleanText(data.message, 300);
  const startsAtMs = Number(data.startsAtMs || 0);
  const endsAtMs = Number(data.endsAtMs || 0);
  if (!startsAtMs || !endsAtMs || endsAtMs <= startsAtMs) throw new Error("End date and time must be after the start date and time.");
  if (!data.mediaId || !data.storagePath) throw new Error("Celebration media could not be prepared.");

  const ref = doc(getTvDisplayCelebrationsCollectionRef());
  await setDoc(ref, {
    celebrationId: ref.id,
    title,
    type,
    headline,
    message,
    mediaId: String(data.mediaId || ""),
    mediaUrl: String(data.mediaUrl || ""),
    storagePath: String(data.storagePath || ""),
    mimeType: String(data.mimeType || ""),
    backgroundColor: validColor(data.backgroundColor, "#170A10"),
    textColor: validColor(data.textColor, "#FFFFFF"),
    accentColor: validColor(data.accentColor, "#FF4F12"),
    startsAtMs,
    endsAtMs,
    state: CELEBRATION_STATES.DRAFT,
    targetCount: 0,
    version: 1,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function publishCelebration(celebration, devices = []) {
  return manageCelebration(celebration, devices, "SCHEDULE");
}

export async function playCelebrationNow(celebration, devices = []) {
  return manageCelebration(celebration, devices, "PLAY_NOW");
}

export async function pauseCelebration(celebration, devices = []) {
  return manageCelebration(celebration, devices, "PAUSE");
}

export async function deleteCelebration(celebration) {
  if (!celebration?.id) throw new Error("Celebration is required.");
  if (celebration.state === CELEBRATION_STATES.ACTIVE) throw new Error("Pause this celebration before deleting it.");
  await deleteDoc(doc(getTvDisplayCelebrationsCollectionRef(), celebration.id));
}

export async function attachCelebrationMedia(celebrationId, media = {}) {
  if (!celebrationId || !media.mediaId || !media.storagePath) throw new Error("Celebration media is incomplete.");
  await setDoc(doc(getTvDisplayCelebrationsCollectionRef(), celebrationId), {
    mediaId: String(media.mediaId),
    mediaUrl: String(media.mediaUrl || ""),
    storagePath: String(media.storagePath),
    mimeType: String(media.mimeType || ""),
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

async function manageCelebration(celebration, devices, action) {
  if (!celebration?.id) throw new Error("Celebration is required.");
  const deviceIds = [...new Set(devices.filter((device) => device.enabled !== false && device.pairingStatus === "PAIRED").map((device) => device.deviceId || device.id).filter(Boolean))];
  if (action !== "PAUSE" && !deviceIds.length) throw new Error("No paired and enabled TVs are available.");
  const result = await callManageCelebration({ celebrationId: celebration.id, action, deviceIds });
  return result.data || {};
}

function normalizeCelebration(snapshot) {
  const data = snapshot.data() || {};
  return {
    id: snapshot.id,
    ...data,
    title: String(data.title || ""),
    headline: String(data.headline || ""),
    message: String(data.message || ""),
    type: String(data.type || CELEBRATION_TYPES.TEXT),
    state: String(data.state || CELEBRATION_STATES.DRAFT),
    mediaUrl: String(data.mediaUrl || ""),
    startsAtMs: Number(data.startsAtMs || 0),
    endsAtMs: Number(data.endsAtMs || 0),
    version: Math.max(1, Number(data.version || 1)),
    targetCount: Math.max(0, Number(data.targetCount || 0)),
  };
}

function cleanText(value, max) { return String(value || "").trim().slice(0, max); }
function requiredText(value, max, label) { const text = cleanText(value, max); if (!text) throw new Error(`${label} is required.`); return text; }
function validColor(value, fallback) { return /^#[0-9a-f]{6}$/i.test(String(value || "")) ? String(value).toUpperCase() : fallback; }
